import { useQuery } from '@apollo/client';
import i18n from 'i18next';
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';

import AppLoadingScreen from './AppLoadingScreen.tsx';
import { ANNOUNCEMENTS_UPDATE_INTERVAL } from './constants';
import { GET_ANNOUNCEMENTS } from './modules/announcements/queries';
import { appInsights } from './modules/core/analytics/applicationInsights';
import { loadAppEvent } from './modules/core/analytics/customEvents';
import PageError from './modules/core/components/PageError.tsx';
import NotFound from './modules/core/components/PageNotFound.tsx';
import { ApplicationContextProvider } from './modules/core/contexts/ApplicationContext';
import featureConfig from './modules/core/featureToggles';
import { useAuth } from './modules/core/utils/authentication';
import { FeatureTogglesProvider } from './modules/core/utils/hooks/useFeatureToggle';
import {
    skipPollAttemptWhenHidden,
    useRefetchOnTabVisible,
} from './modules/core/utils/hooks/useRefetchOnTabVisible.ts';
import { modules } from './modules/core/utils/modules';
import { DarkModeProvider } from './modules/core/utils/useDarkMode.tsx';
import { StoreProvider } from './modules/document/stores';

// order is important here
import '../stylesheets/tailwind.css';
import './app.css';
import { FeatureFlagProvider } from './modules/core/utils/hooks/useFeatureFlag.tsx';

export const DARK_MODE_LOCAL_STORAGE_KEY = 'darkMode';

interface AuthContextValue {
    user: ReturnType<typeof useAuth>['user'];
    loggedIn: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, loggedIn: false });

const AppRoutes = () => {
    const { user, loggedIn } = useContext(AuthContext);
    return (
        <StoreProvider>
            <Routes>
                {modules.renderRoutes({
                    user,
                    loggedIn,
                })}
                <Route element={<NotFound />} path="*" />
            </Routes>
        </StoreProvider>
    );
};

// a data router, so a page can hold back navigation (useBlocker); the modules' routes stay in AppRoutes
const router = createBrowserRouter([{ path: '*', element: <AppRoutes />, errorElement: <PageError /> }]);

const App = () => {
    const { t } = useTranslation();

    const { user, refetchUser, loggedIn, loading } = useAuth();
    // polling once a minute for new announcements should be enough; hidden tabs skip polls and refresh on return
    const { data: announcementsData, refetch: refetchAnnouncements } = useQuery(GET_ANNOUNCEMENTS, {
        pollInterval: ANNOUNCEMENTS_UPDATE_INTERVAL,
        skipPollAttempt: skipPollAttemptWhenHidden,
    });
    useRefetchOnTabVisible(refetchAnnouncements);

    // this helps the navigation to know when it should be collapsed
    const [activeChannelId, setActiveChannelId] = useState(null);

    useEffect(() => {
        appInsights?.trackEvent(...loadAppEvent(user));

        if (!loggedIn) return;

        if (user.language) {
            void i18n.changeLanguage(user.language.toLowerCase());
        }
    }, [loggedIn]);

    return (
        <ApplicationContextProvider
            user={user}
            refetchUser={refetchUser}
            loading={loading}
            channelId={activeChannelId}
            setChannelId={setActiveChannelId}
            announcements={announcementsData?.announcements || []}
        >
            <FeatureFlagProvider sdkKey={process.env.CONFIGCAT_SDK_KEY}>
                <FeatureTogglesProvider featureConfig={featureConfig}>
                    <DarkModeProvider>
                        <>
                            <title>{t('app.title')}</title>

                            {loading ? ( // TODO remove loading state handling here & leave it up to shells
                                <AppLoadingScreen />
                            ) : (
                                <AuthContext.Provider value={{ user, loggedIn }}>
                                    <RouterProvider router={router} />
                                </AuthContext.Provider>
                            )}
                        </>
                    </DarkModeProvider>
                </FeatureTogglesProvider>
            </FeatureFlagProvider>
        </ApplicationContextProvider>
    );
};

export default App;
